import { Send } from "@mui/icons-material";
import { LoadingButton } from "@mui/lab";
import { Box, Chip, Stack, Typography, Button, Snackbar, Alert } from "@mui/material";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { getCandidateApplications } from "app/api/application_api";
import { H2, H3 } from "app/components/Typography";
import { usePostApplication } from "app/hooks/application/usePostApplication";
import useGetCandidate from "app/hooks/candidate/useGetCandidate";
import { useGetCompany } from "app/hooks/company/useGetCompany";
import useAuth from "app/hooks/useAuth";
import useNotification from "app/hooks/useNotification";
import { AppRoutes, getFormattedExperience } from "app/utils/global_constants";
import { useMemo, useState, useEffect } from "react";
import { Link } from "react-router-dom";
import CompanyDescription from "./Apply Dialog/CompanyDescription";
import JobDescription from "./Apply Dialog/JobDescription";
import DetailAccordian from "./DetailAccordian";
import LocationSelect from "./Job Detail/LocationSelect";
import { LocationOptions } from "../Constants";

function isSalaryNotDisclosed(minSalary, maxSalary) {
  return Number(minSalary) === 0 && Number(maxSalary) === 0;
}

function ApplyDialogContent2({ job, onClose, onApplied }) {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const { createNotification } = useNotification();
  const [applyError, setApplyError] = useState(false);
  const [preferredLocation, setPreferredLocation] = useState([]);
  const [snackbarNotification, setSnackbarNotification] = useState({
    open: false,
    message: "",
    severity: "success"
  });
  const [isApplied, setIsApplied] = useState(false);

  // Process location options using useMemo
  const locationOptions = useMemo(() => {
    if (!job?.jp_city) {
      return [];
    }

    // Handle different formats of city data
    let cities = [];
    if (typeof job.jp_city === "string") {
      // Split by pipe and handle both formats: "City1| City2" and "City1|City2"
      cities = job.jp_city.split(/\s*\|\s*/);
    } else if (Array.isArray(job.jp_city)) {
      cities = job.jp_city;
    } else {
      return [];
    }

    // Process each city
    const options = cities
      .map((loc) => {
        if (!loc) return null;
        const trimmed = String(loc).trim();
        if (!trimmed) return null;

        // If location contains a hyphen, take the part after the hyphen
        const parts = trimmed.split("-");
        return parts.length > 1 ? parts[1].trim() : trimmed;
      })
      .filter(Boolean);

    return options;
  }, [job?.jp_city]);

  const citiesListString = useMemo(() => {
    const result = locationOptions.join(", ");
    return result;
  }, [locationOptions]);

  // Move all hooks before any conditional returns
  const { data: candidate, isFetched: isCandidateFetched } = useGetCandidate(user?.id, "");

  const { data: applications, isFetched: isApplyFetched } = useQuery({
    queryKey: ["applicationByCandidate"],
    queryFn: () => getCandidateApplications(user?.id),
    staleTime: Infinity,
    enabled: !!user?.id
  });

  const { data: company } = useGetCompany({ id: job?.jp_company_id });

  // Update isApplied state when applications data changes
  useEffect(() => {
    if (applications && job) {
      const hasApplied = applications.some((app) => app.jp_jobpost_id === job.jp_id);
      setIsApplied(hasApplied);
      console.log("Application status updated:", hasApplied);
    }
  }, [applications, job]);

  const handleCloseNotification = () => {
    setSnackbarNotification((prev) => ({ ...prev, open: false }));
  };

  const {
    mutate: postApplication,
    isPending: isApplying,
    isSuccess
  } = usePostApplication({
    onApply: () => {},
    onSuccess: async (data) => {
      createNotification(notification, "admin");
      setIsApplied(true);
      setSnackbarNotification({
        open: true,
        message: "Successfully applied for the job!",
        severity: "success"
      });
      queryClient.invalidateQueries({ queryKey: ["job_listings"] });
      queryClient.invalidateQueries({ queryKey: ["CandidateApplication"] });
      if (onApplied) onApplied();
      setTimeout(() => {
        onClose();
      }, 2000);
    },
    onError: (error) => {
      console.error("Error submitting application:", error);
      setApplyError(error.message || "Failed to apply. Please try again later.");
      setSnackbarNotification({
        open: true,
        message: error.message || "Failed to apply for the job. Please try again.",
        severity: "error"
      });
    }
  });

  // Now we can do the conditional return
  if (!job) {
    return (
      <Box p={2}>
        <Typography color="error">No job data available. Please try again.</Typography>
      </Box>
    );
  }

  const notification = {
    jp_title: `New Application`,
    jp_message: `${user?.name || "A candidate"} has applied for the role of ${job.jp_title || "this position"}`,
    jp_status: "pending"
  };

  // const companyNotification = {
  //   jp_title: `New Application`,
  //   jp_message: `${user?.name || 'A candidate'} has applied for the role of ${job.jp_title || 'this position'}`,
  //   jp_company_id: job.jp_company_id
  // };

  const application = {
    jp_company_id: job.jp_company_id,
    jp_jobpost_id: job.jp_id,
    jp_candidate_id: user?.id,
    jp_status: "pending",
    jp_is_share_to_company: 0,
    jp_location_preference: preferredLocation.join("|"),
    mail: {
      jp_candidate_name: user?.name || "",
      jp_email: user?.email || "",
      jp_job_title: job.jp_title || "",
      jp_company_email: job.company?.jp_email || "",
      jp_phone: candidate ? candidate.jp_contactno : "Not specified",
      jp_company_name: company ? company.jp_company_name : ""
    }
  };

  // Add validation before submission
  const validateApplication = () => {
    if (!user?.id) {
      setApplyError("User information is missing. Please try logging in again.");
      return false;
    }
    if (!job.jp_company_id) {
      setApplyError("Company information is missing.");
      return false;
    }
    if (!job.jp_id) {
      setApplyError("Job information is missing.");
      return false;
    }
    if (!isRemoteWork && preferredLocation.length === 0) {
      setApplyError("Please select at least one location preference.");
      return false;
    }
    return true;
  };

  const handleSubmit = () => {
    console.log("Submitting application:", application);
    if (validateApplication()) {
      postApplication(application);
    }
  };

  const requiredSkills = job.jp_required_skills
    ? job.jp_required_skills
        .split("| ")
        .map((skill) => (
          <Chip
            key={skill}
            label={skill}
            sx={{ marginRight: "5px", bgcolor: "green", color: "white" }}
          />
        ))
    : ["No skill specified"];

  const noResume = isCandidateFetched && !candidate?.jp_resume && !isApplied;
  const canApply = isApplyFetched && !isApplied && isCandidateFetched && candidate?.jp_resume;
  const isJobSalaryNotDisclosed = isSalaryNotDisclosed(
    job.jp_minimum_salary,
    job.jp_maximum_salary
  );
  const isRemoteWork = job?.jp_location?.trim() === LocationOptions[1].label;
  const disableSubmit = !isRemoteWork && preferredLocation.length === 0;

  return (
    <>
      <Box display={"flex"} width={"100%"} flexDirection={"column"} gap={2} p={2}>
        {/* Company Description */}
        <CompanyDescription company={company} job={job} />
        {/* Job Description*/}
        <JobDescription cities={citiesListString} description={job.jp_description} />
        <Box display="flex" flexDirection="column" gap={1}>
          <DetailAccordian
            title={"Requirements"}
            details={[
              <Typography key={"qualification"}>
                Qualifications: {job.jp_qualification || "Not specified"}
              </Typography>,
              <Typography key={"experience"}>
                Experience: {getFormattedExperience(job.jp_min_exp, job.jp_max_exp)}
              </Typography>,
              <Typography key={"location"}>
                Location Type: {job.jp_location || "Not specified"}
              </Typography>
            ]}
          />
          <DetailAccordian
            title={"Salary Information"}
            details={
              isJobSalaryNotDisclosed
                ? [<Typography key={"not_disclosed"}>Not Disclosed</Typography>]
                : [
                    <Typography key={"min_salary"}>
                      Starting CTC: Rs. {Number(job.jp_minimum_salary || 0).toLocaleString("en-IN")}
                    </Typography>,
                    <Typography key={"max_salary"}>
                      Maximum CTC: Rs. {Number(job.jp_maximum_salary || 0).toLocaleString("en-IN")}
                    </Typography>
                  ]
            }
          />
          <DetailAccordian
            title={"Benefits"}
            details={[
              <Box display={"flex"} flexWrap={"wrap"} key={"benefits"} gap={1}>
                {job.Benefits?.length > 0 &&
                  job.Benefits.map((benefit) => (
                    <Chip
                      key={benefit.jp_id}
                      label={benefit.jp_benefit}
                      sx={{ marginRight: "5px", bgcolor: "green", color: "white" }}
                    />
                  ))}
                {!job.Benefits?.length && <Typography>Not specified</Typography>}
              </Box>
            ]}
          />
          <DetailAccordian
            title={"Skills Required"}
            details={[
              <Box display={"flex"} flexWrap={"wrap"} key={"benefits"} gap={1}>
                {requiredSkills}
              </Box>
            ]}
          />
        </Box>
        {locationOptions.length > 0 && !isRemoteWork && (
          <Stack mt={1} gap={1}>
            <Typography fontWeight={"bold"}>Your location preference is required: </Typography>
            <LocationSelect
              value={preferredLocation}
              locations={locationOptions}
              onChange={(newValue) => {
                setPreferredLocation(newValue);
                setApplyError(false);
              }}
              error={applyError}
              helperText={applyError}
            />
          </Stack>
        )}
        <Box display={"flex"} gap={2} justifyContent={"end"} alignItems={"center"}>
          {isApplied && (
            <H2
              sx={{
                color: "#5a8da1",
                backgroundColor: "lightblue",
                padding: "4px 6px 4px 6px",
                borderRadius: "5px",
                width: "100%",
                textAlign: "center"
              }}
            >
              Applied
            </H2>
          )}
          {noResume && (
            <Box display={"flex"} flexDirection={"column"} gap={1} width={"100%"}>
              <Typography color={"error"}>
                Please upload your resume to apply for this job
              </Typography>
              <Button
                variant="contained"
                component={Link}
                to={AppRoutes.ROUTE_PROFILE}
                sx={{ width: "fit-content" }}
              >
                Upload Resume
              </Button>
            </Box>
          )}
          {canApply && (
            <LoadingButton
              variant="contained"
              loading={isApplying}
              disabled={disableSubmit}
              onClick={handleSubmit}
              endIcon={<Send />}
            >
              Apply
            </LoadingButton>
          )}
        </Box>
      </Box>
      <Snackbar
        open={snackbarNotification.open}
        autoHideDuration={6000}
        onClose={handleCloseNotification}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={handleCloseNotification}
          severity={snackbarNotification.severity}
          sx={{ width: "100%" }}
        >
          {snackbarNotification.message}
        </Alert>
      </Snackbar>
    </>
  );
}

export default ApplyDialogContent2;
